import React, { useState, useEffect, useRef } from 'react';
import { Sun, Moon, MapPin, X, AlertCircle, CheckCircle2, Sparkles, Calendar, Search, Navigation, ChevronDown, ChevronUp, Compass, Clock } from 'lucide-react';
import { Language } from '../types/kundali';
import { usePanchang } from '../hooks/usePanchang';
import {
  CANONICAL_CITY_PRESETS,
  searchCanonicalLocations,
  VerifiedLocation,
  getUserLocationPref,
  setUserLocationPref
} from '../services/geoService';

export interface PanchangDrawerProps {
  onClose: () => void;
  initialCity?: string;
  lang?: Language | string;
  onCityChange?: (city: string) => void;
}

// 60-year Jovian Samvatsara Cycle
const BRIHASPATI_SAMVATSARAS = [
  'Prabhava', 'Vibhava', 'Shukla', 'Pramoda', 'Prajapati', 'Angirasa', 'Srimukha', 'Bhava', 'Yuva', 'Dhatri',
  'Ishwara', 'Bahudhanya', 'Pramathi', 'Vikrama', 'Vrisha', 'Chitrabhanu', 'Subhanu', 'Tarana', 'Parthiva', 'Vyaya',
  'Sarvajitu', 'Sarvadhari', 'Virodhi', 'Vikriti', 'Khara', 'Nandana', 'Vijaya', 'Jaya', 'Manmatha', 'Durmukhi',
  'Hemalambi', 'Vilambi', 'Vikari', 'Sharvari', 'Plava', 'Shubhakritu', 'Shobhana', 'Krodhi', 'Vishwavasu', 'Parabhava',
  'Plavanga', 'Kilaka', 'Saumya', 'Sadharana', 'Virodhikritu', 'Paridhavi', 'Pramathi', 'Ananda', 'Rakshasa', 'Nala',
  'Pingala', 'Kala', 'Siddharthati', 'Raudra', 'Durmati', 'Dundubhi', 'Rudhirodgari', 'Raktakshi', 'Krodhana', 'Kshaya'
];

const RITUS = [
  { nameEn: 'Vasanta (Spring)', nameHi: 'वसन्त ऋतु', months: ['Chaitra', 'Vaishakha'] },
  { nameEn: 'Grishma (Summer)', nameHi: 'ग्रीष्म ऋतु', months: ['Jyeshtha', 'Ashadha'] },
  { nameEn: 'Varsha (Monsoon)', nameHi: 'वर्षा ऋतु', months: ['Shravana', 'Bhadrapada'] },
  { nameEn: 'Sharad (Autumn)', nameHi: 'शरद ऋतु', months: ['Ashvina', 'Kartika'] },
  { nameEn: 'Hemanta (Pre-Winter)', nameHi: 'हेमन्त ऋतु', months: ['Margashirsha', 'Pausha'] },
  { nameEn: 'Shishira (Winter)', nameHi: 'शिशिर ऋतु', months: ['Magha', 'Phalguna'] }
];

const DISH_SHOOL: Record<number, { en: string; hi: string }> = {
  0: { en: 'West (Paschim) - Avoid travel or carry ginger/ghee remedy', hi: 'पश्चिम दिशा - यात्रा वर्जित (अतः घी खाकर निकलें)' },
  1: { en: 'East (Purva) - Avoid travel or carry milk remedy', hi: 'पूर्व दिशा - यात्रा वर्जित (अतः दूध पीकर निकलें)' },
  2: { en: 'North (Uttar) - Avoid travel or carry honey remedy', hi: 'उत्तर दिशा - यात्रा वर्जित (अतः शहद खाकर निकलें)' },
  3: { en: 'North (Uttar) - Avoid travel or carry milk remedy', hi: 'उत्तर दिशा - यात्रा वर्जित (अतः दूध खाकर निकलें)' },
  4: { en: 'South (Dakshin) - Avoid travel or carry curd remedy', hi: 'दक्षिण दिशा - यात्रा वर्जित (अतः दही खाकर निकलें)' },
  5: { en: 'West (Paschim) - Avoid travel or carry barley remedy', hi: 'पश्चिम दिशा - यात्रा वर्जित (अतः जौ खाकर निकलें)' },
  6: { en: 'East (Purva) - Avoid travel or carry sesame remedy', hi: 'पूर्व दिशा - यात्रा वर्जित (अतः तिल खाकर निकलें)' }
};

export const PanchangDrawer: React.FC<PanchangDrawerProps> = ({
  onClose,
  initialCity,
  lang = 'en',
  onCityChange
}) => {
  const isHi = lang === 'hi';
  const isSa = lang === 'sa';
  const isPa = lang === 'pa';

  // Selected date state for past or future Panchang evaluation (defaulting to today)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showAdvancedSection, setShowAdvancedSection] = useState(false);

  // Active verified location state
  const [selectedLocation, setSelectedLocation] = useState<VerifiedLocation>(() => {
    const userPref = getUserLocationPref();
    if (initialCity && initialCity.trim() !== '' && initialCity !== 'Indore, MP' && initialCity !== userPref.name) {
      const match = CANONICAL_CITY_PRESETS.find(
        (p) => p.name.toLowerCase() === initialCity.toLowerCase() || initialCity.toLowerCase().includes(p.name.toLowerCase())
      );
      if (match) return match;
    }
    return userPref;
  });

  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<VerifiedLocation[]>(CANONICAL_CITY_PRESETS.slice(0, 6));
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Bind calculation to selected verified location and selected date
  const panchang = usePanchang(selectedLocation, selectedDate);

  // Listen for global location preference change
  useEffect(() => {
    const handleLocChanged = (e: Event) => {
      const detail = (e as CustomEvent<VerifiedLocation>).detail;
      if (detail) {
        setSelectedLocation(detail);
      }
    };
    window.addEventListener('astro_location_pref_changed', handleLocChanged);
    return () => window.removeEventListener('astro_location_pref_changed', handleLocChanged);
  }, []);

  // Sync initial city when updated
  useEffect(() => {
    if (initialCity && initialCity !== selectedLocation.name) {
      const match = CANONICAL_CITY_PRESETS.find(
        (p) => p.name.toLowerCase() === initialCity.toLowerCase() || initialCity.toLowerCase().includes(p.name.toLowerCase())
      );
      if (match) {
        setSelectedLocation(match);
      }
    }
  }, [initialCity]);

  // Debounced search for verified canonical locations
  useEffect(() => {
    if (!isSearchingLocation) return;
    const timer = setTimeout(async () => {
      setIsLoadingResults(true);
      try {
        const results = await searchCanonicalLocations(searchQuery);
        setSearchResults(results);
      } catch (err) {
        console.error('Location search failed:', err);
      } finally {
        setIsLoadingResults(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, isSearchingLocation]);

  // Outside click & ESC dismissal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleSelectVerifiedLocation = (loc: VerifiedLocation) => {
    setSelectedLocation(loc);
    setUserLocationPref(loc);
    setIsSearchingLocation(false);
    setSearchQuery('');
    if (onCityChange) {
      onCityChange(loc.name);
    }
  };

  // Derived Astrologer attributes for selected date
  const dayOfWeek = selectedDate.getDay();
  const dishShoolInfo = DISH_SHOOL[dayOfWeek] || DISH_SHOOL[0];
  const rituObj = RITUS.find(r => r.months.includes(panchang.masa)) || RITUS[0];
  const brihaspatiSamvatsara = BRIHASPATI_SAMVATSARAS[(panchang.vikramSamvat + 11) % 60];
  const ayana = selectedDate.getMonth() >= 3 && selectedDate.getMonth() <= 8 ? (isHi ? 'उत्तरायण (Uttarayan)' : 'Uttarayan (Northern Course)') : (isHi ? 'दक्षिणायन (Dakshinayan)' : 'Dakshinayan (Southern Course)');
  
  // Mantri Mandala estimation based on Vikram Samvat weekday of Chaitra Shukla 1
  const samvatLords = ['Sun (Surya - King)', 'Moon (Chandra - Minister)', 'Mars (Mangal - Commander)', 'Mercury (Budh - General)', 'Jupiter (Guru - Finance)', 'Venus (Shukra - Crops)', 'Saturn (Shani - Judiciary)'];
  const kingLord = samvatLords[panchang.vikramSamvat % 7];
  const ministerLord = samvatLords[(panchang.vikramSamvat + 3) % 7];

  return (
    <div
      id="todays-panchang-drawer"
      ref={drawerRef}
      role="region"
      aria-label="Astrologer Panchang Cockpit Drawer"
      className="fixed left-0 right-0 top-[52px] sm:top-[56px] z-[100] bg-astro-card border-b border-astro-border shadow-2xl animate-fade-in backdrop-blur-md bg-astro-card/98 transition-all max-h-[90vh] overflow-y-auto"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-8 py-3.5 sm:py-4 space-y-3.5">
        
        {/* ========================================================================= */}
        {/* PROMINENT DATE BANNER & DATE PICKER HEADER                                */}
        {/* ========================================================================= */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-3">
          
          <div className="flex items-center gap-3">
            {/* Clickable Date Picker Badge */}
            <div className="relative group">
              <div className="w-13 h-13 rounded-xl bg-astro-bg border border-astro-border shadow-sm flex flex-col items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:border-astro-accent transition">
                <span className="w-full bg-astro-primary text-astro-bg text-[9px] font-bold uppercase tracking-wider text-center py-0.5">
                  {panchang.monthShort}
                </span>
                <span className="text-base font-extrabold text-astro-primary font-mono leading-tight mt-0.5">
                  {panchang.dayNumber}
                </span>
              </div>
              <input
                type="date"
                value={`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`}
                onChange={(e) => {
                  if (e.target.value) {
                    const [y, m, d] = e.target.value.split('-').map(Number);
                    if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
                      setSelectedDate(new Date(y, m - 1, d));
                    }
                  }
                }}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                title="Click to select past or future date"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-serif font-bold text-base sm:text-lg text-astro-primary tracking-tight leading-tight">
                  {panchang.fullGregorianDate}
                </h3>
                {/* Direct Visible Date Picker Input */}
                <div className="flex items-center gap-1.5 bg-astro-bg px-2 py-1 rounded-lg border border-astro-border">
                  <Calendar className="w-3.5 h-3.5 text-astro-accent shrink-0" />
                  <input
                    type="date"
                    value={`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`}
                    onChange={(e) => {
                      if (e.target.value) {
                        const [y, m, d] = e.target.value.split('-').map(Number);
                        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
                          setSelectedDate(new Date(y, m - 1, d));
                        }
                      }
                    }}
                    className="text-xs font-mono font-bold text-astro-primary bg-transparent focus:outline-none cursor-pointer"
                  />
                </div>
              </div>

              <div className="text-xs sm:text-sm font-medium text-astro-accent tracking-wide mt-0.5 flex items-center gap-2 flex-wrap">
                <span>{panchang.samvatString}</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-50 text-amber-900 border border-amber-200 font-mono">
                  Samvatsara: {brihaspatiSamvatsara}
                </span>
                {panchang.isDualTithi && (
                  <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 font-bold animate-pulse">
                    ⚡ Dual Tithi Transition Day
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto">
            <button
              type="button"
              onClick={() => setSelectedDate(new Date())}
              className="text-xs font-bold text-astro-primary bg-astro-bg px-2.5 py-1 rounded-lg border border-astro-border cursor-pointer hover:bg-astro-card transition"
            >
              {isHi ? 'आज (Today)' : 'Reset to Today'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-xs font-bold text-astro-secondary-text hover:text-astro-primary bg-astro-bg px-3 py-1.5 rounded-lg border border-astro-border cursor-pointer hover:bg-[#FAF6ED] transition shadow-2xs"
            >
              <span>{isHi ? 'बंद करें' : 'Close'}</span>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* ASTROLOGER DAY-TO-DAY COCKPIT GRID                                        */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          
          {/* LEFT COLUMN: Location, Solar & Lunar Rise/Set, Mantri Mandala (5 cols) */}
          <div className="lg:col-span-5 flex flex-col gap-2.5 bg-astro-bg p-3 rounded-xl border border-astro-border shadow-2xs">
            
            {/* Verified Location Bar */}
            <div className="border-b border-astro-border pb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-muted flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-astro-accent" />
                  {isHi ? 'स्थान एवं कोऑर्डिनेट्स' : 'Location & Verified Coords'}
                </span>
                {!isSearchingLocation ? (
                  <button
                    type="button"
                    onClick={() => setIsSearchingLocation(true)}
                    className="text-[11px] font-bold text-astro-accent hover:underline cursor-pointer flex items-center gap-1"
                  >
                    <Search className="w-3 h-3" />
                    <span>{isHi ? 'स्थान बदलें' : 'Change'}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsSearchingLocation(false)}
                    className="text-[11px] text-astro-muted hover:underline cursor-pointer"
                  >
                    {isHi ? 'रद्द' : 'Cancel'}
                  </button>
                )}
              </div>

              {isSearchingLocation ? (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-astro-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={isHi ? 'शहर खोजें...' : 'Search city...'}
                      className="w-full pl-8 pr-3 py-1 text-xs rounded-lg border border-astro-border bg-[#FAF6ED] text-astro-text"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-36 overflow-y-auto divide-y divide-astro-border border border-astro-border rounded-lg bg-astro-bg">
                    {searchResults.map((loc, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSelectVerifiedLocation(loc)}
                        className="w-full text-left px-2.5 py-1 hover:bg-[#FAF6ED] transition cursor-pointer flex items-center justify-between text-xs"
                      >
                        <span className="font-bold text-astro-primary">{loc.name}</span>
                        <span className="text-[10px] font-mono text-astro-accent">{loc.latitude.toFixed(2)}°, {loc.longitude.toFixed(2)}°</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-astro-primary text-sm flex items-center gap-1">
                    <Navigation className="w-3 h-3 text-astro-accent" />
                    {selectedLocation.name}
                  </span>
                  <span className="font-mono text-[11px] text-astro-accent font-semibold">
                    {panchang.verifiedCoordinatesString}
                  </span>
                </div>
              )}
            </div>

            {/* Sun & Moon Rise/Set Cards */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border">
                <div className="flex items-center gap-1.5 text-astro-accent font-bold mb-0.5">
                  <Sun className="w-3.5 h-3.5" />
                  <span>{isHi ? 'सूर्य' : 'Solar Times'}</span>
                </div>
                <div className="space-y-0.5 font-mono text-[11px]">
                  <div>Rise: <strong>{panchang.sunrise}</strong></div>
                  <div>Set: <strong>{panchang.sunset}</strong></div>
                </div>
              </div>

              <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border">
                <div className="flex items-center gap-1.5 text-astro-primary font-bold mb-0.5">
                  <Moon className="w-3.5 h-3.5" />
                  <span>{isHi ? 'चन्द्र' : 'Lunar Times'}</span>
                </div>
                <div className="space-y-0.5 font-mono text-[11px]">
                  <div>Rise: <strong>06:45 AM</strong></div>
                  <div>Set: <strong>08:15 PM</strong></div>
                </div>
              </div>
            </div>

            {/* Ritu, Ayana & Vikram Samvat Mantri Mandala */}
            <div className="p-2.5 rounded-lg bg-[#FAF6ED] border border-astro-border space-y-1.5 text-xs">
              <span className="font-bold text-astro-primary block text-[11px] uppercase tracking-wider border-b pb-1">
                {isHi ? 'ऋतु, अयन एवं मन्त्री मण्डल (Vikram Samvat Lords)' : 'Ritu, Ayana & Samvat Mantri Mandala'}
              </span>
              <div className="grid grid-cols-2 gap-y-1 text-[11px]">
                <span className="text-astro-muted">Ritu (Season):</span>
                <span className="font-semibold">{isHi ? rituObj.nameHi : rituObj.nameEn}</span>
                <span className="text-astro-muted">Ayana:</span>
                <span className="font-semibold">{ayana}</span>
                <span className="text-astro-muted">Samvat King (Raja):</span>
                <span className="font-bold text-astro-primary">{kingLord}</span>
                <span className="text-astro-muted">Samvat Minister:</span>
                <span className="font-bold text-astro-accent">{ministerLord}</span>
              </div>
            </div>

            {/* Dish Shool (Dir Shool) */}
            <div className="p-2.5 rounded-lg bg-amber-50/70 border border-amber-200 text-xs space-y-1">
              <div className="flex items-center gap-1.5 font-bold text-amber-900 text-[11px]">
                <Compass className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                <span>{isHi ? 'दिश शूल (Dish Shool)' : 'Directional Shool (Travel Alert)'}</span>
              </div>
              <p className="text-[11px] text-amber-800 leading-tight">
                {isHi ? dishShoolInfo.hi : dishShoolInfo.en}
              </p>
            </div>
          </div>

          {/* RIGHT COLUMN: 5 Panchang Limbs, Timings & Fasting (7 cols) */}
          <div className="lg:col-span-7 flex flex-col gap-2.5">
            
            {/* 5 Panchang Limbs Grid */}
            <div className="bg-astro-bg p-3 rounded-xl border border-astro-border shadow-2xs space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-astro-muted flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? 'पञ्चाङ्ग पञ्च-अंग (दैनिक काल स्थिति)' : "Panchang Essentials (Five Limbs of Time)"}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border">
                  <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">Tithi</span>
                  {panchang.isDualTithi && panchang.tithiSegments ? (
                    <div className="space-y-1 mt-1">
                      {panchang.tithiSegments.map((seg, sIdx) => (
                        <div key={sIdx} className="text-[11px] border-b border-astro-border/50 pb-0.5 last:border-0">
                          <strong className="text-astro-primary block font-serif">{seg.name}</strong>
                          <span className="text-[9px] text-astro-secondary-text font-mono">{seg.timeWindow}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <strong className="text-astro-primary font-serif block text-xs truncate">{panchang.tithi}</strong>
                      <span className="text-[10px] text-astro-secondary-text block truncate">{panchang.tithiEnd}</span>
                    </>
                  )}
                </div>

                <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border">
                  <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">Nakshatra</span>
                  <strong className="text-astro-text block text-xs truncate">{panchang.nakshatra}</strong>
                  <span className="text-[10px] text-astro-secondary-text block truncate">{panchang.nakshatraEnd}</span>
                </div>

                <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border">
                  <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">Yoga</span>
                  <strong className="text-astro-text block text-xs truncate">{panchang.yoga}</strong>
                  <span className="text-[10px] text-astro-muted block">Auspicious</span>
                </div>

                <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border">
                  <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">Karana</span>
                  <strong className="text-astro-text block text-xs truncate">{panchang.karana}</strong>
                  <span className="text-[10px] text-astro-muted block">Movable</span>
                </div>

                <div className="p-2 rounded-lg bg-[#FAF6ED] border border-astro-border col-span-2 sm:col-span-1">
                  <span className="text-[9px] uppercase tracking-wider text-astro-muted block font-semibold">Vaara (Day)</span>
                  <strong className="text-astro-primary block text-xs truncate">{panchang.vaara}</strong>
                  <span className="text-[10px] text-astro-muted block">{panchang.paksha}</span>
                </div>
              </div>
            </div>

            {/* Auspicious & Inauspicious Timings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="p-2.5 rounded-xl bg-emerald-50/80 border border-emerald-200 text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-bold text-emerald-900">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                  <span>{isHi ? 'शुभ मुहूर्त (Auspicious)' : 'Auspicious Windows'}</span>
                </div>
                <div className="space-y-0.5 text-[11px] font-mono">
                  <div>Abhijit: <strong>{panchang.abhijitMuhurat}</strong></div>
                  <div>Amrit Kaal: <strong>10:15 AM - 11:50 AM</strong></div>
                </div>
              </div>

              <div className="p-2.5 rounded-xl bg-amber-50/80 border border-amber-200 text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-bold text-amber-900">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-700" />
                  <span>{isHi ? 'अशुभ काल (Inauspicious)' : 'Inauspicious Windows'}</span>
                </div>
                <div className="space-y-0.5 text-[11px] font-mono">
                  <div>Rahu Kaal: <strong>{panchang.rahuKaal}</strong></div>
                  <div>Yamaganda: <strong>12:00 PM - 01:30 PM</strong></div>
                </div>
              </div>
            </div>

            {/* Pitru Paksha & Shraddha Mahatmya Card (When Active) */}
            {panchang.pitruPaksha?.isActive && (
              <div className="bg-amber-50/70 border border-amber-300 rounded-xl p-3 shadow-2xs space-y-2 text-xs animate-fade-in">
                <div className="flex items-center justify-between flex-wrap gap-1 border-b border-amber-200/80 pb-1.5">
                  <span className="font-serif font-bold text-amber-950 text-xs">
                    {isHi ? (panchang.pitruPaksha.observanceNameHi || 'पितृ पक्ष (श्राद्ध पक्ष)') : 'Pitru Paksha (Shraddha Paksha)'}
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.2 rounded bg-amber-200 text-amber-900 border border-amber-300 font-mono">
                    {isHi ? (panchang.pitruPaksha.shraddhaTithiNameHi || panchang.pitruPaksha.shraddhaTithiNameEn) : panchang.pitruPaksha.shraddhaTithiNameEn}
                  </span>
                </div>
                {panchang.pitruPaksha.kutupaMuhurta && (
                  <div className="text-[11px] font-mono text-amber-900 bg-white/80 p-1.5 rounded border border-amber-200 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>{isHi ? 'कुतुप काल: ' : 'Kutupa: '}<strong>{panchang.pitruPaksha.kutupaMuhurta.start} – {panchang.pitruPaksha.kutupaMuhurta.end}</strong></span>
                    <span>{isHi ? 'रोहिण: ' : 'Rohina: '}<strong>{panchang.pitruPaksha.rohinaMuhurta?.start} – {panchang.pitruPaksha.rohinaMuhurta?.end}</strong></span>
                  </div>
                )}
                <p className="text-[11px] text-amber-800 leading-snug">
                  {isHi ? (panchang.pitruPaksha.tarpanGuidanceHi || panchang.pitruPaksha.descriptionHi) : (panchang.pitruPaksha.tarpanGuidanceEn || panchang.pitruPaksha.description)}
                </p>
              </div>
            )}

            {/* Surya Sankranti & Punya Kaal Card (When Active) */}
            {panchang.sankranti?.isSankrantiDay && (
              <div className="bg-orange-50/80 border border-orange-300 rounded-xl p-3 shadow-2xs space-y-2 text-xs animate-fade-in">
                <div className="flex items-center justify-between flex-wrap gap-1 border-b border-orange-200/80 pb-1.5">
                  <span className="font-serif font-bold text-orange-950 text-xs">
                    {isHi ? (panchang.sankranti.sankrantiNameHi || 'सूर्य संक्रांति') : (panchang.sankranti.sankrantiNameEn || 'Surya Sankranti')}
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.2 rounded bg-orange-200 text-orange-900 border border-orange-300 font-mono">
                    {isHi ? `संक्रमण: ${panchang.sankranti.formattedIngressTime}` : `Ingress: ${panchang.sankranti.formattedIngressTime}`}
                  </span>
                </div>
                {panchang.sankranti.formattedPunyaKaal && (
                  <div className="text-[11px] font-mono text-orange-900 bg-orange-100/60 p-1.5 rounded border border-orange-200 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>{isHi ? 'पुण्य काल: ' : 'Punya Kaal: '}<strong>{panchang.sankranti.formattedPunyaKaal}</strong></span>
                    {panchang.sankranti.formattedMahaPunyaKaal && (
                      <span>{isHi ? 'महापुण्य: ' : 'Maha Punya: '}<strong>{panchang.sankranti.formattedMahaPunyaKaal}</strong></span>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-orange-800 leading-snug">
                  {isHi ? panchang.sankranti.significanceHi : panchang.sankranti.significanceEn}
                </p>
              </div>
            )}

            {/* Fasting / Observance Box */}
            <div className="bg-astro-bg p-3 rounded-xl border border-astro-border shadow-2xs space-y-1.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-bold text-astro-primary uppercase tracking-wider text-xs">
                  {panchang.hasVrat ? (isHi ? panchang.vratNameHi : panchang.vratName) : (isHi ? 'दैनिक व्रत स्थिति' : 'Daily Observance & Fasting Status')}
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                  {panchang.hasVrat ? (isHi ? 'व्रत सक्रिय' : 'Vrat Active') : (isHi ? 'सामान्य दिन' : 'Normal Day')}
                </span>
              </div>
              <p className="text-[11px] text-astro-secondary-text italic bg-[#FAF6ED] px-2.5 py-1.5 rounded-lg border border-astro-border">
                {panchang.hasVrat ? (isHi ? panchang.paranaNotesHi : panchang.paranaNotes) : (isHi ? panchang.dailyObservanceHi : panchang.dailyObservance)}
              </p>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* SECONDARY DETAILED SECTION / ACCORDION FOR ADVANCED ASTROLOGERS           */}
        {/* ========================================================================= */}
        <div className="border-t border-astro-border pt-3">
          <button
            type="button"
            onClick={() => setShowAdvancedSection(prev => !prev)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-astro-bg border border-astro-border text-xs font-bold text-astro-primary hover:bg-[#FAF6ED] transition cursor-pointer shadow-2xs"
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'विस्तृत पंचांग, अन्य संवत, आनंदद योग एवं एपhemeris आंकड़े' : 'Detailed Ephemeris, Epochs, Anandadi Yoga & Advanced Calculations'}</span>
            </div>
            {showAdvancedSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showAdvancedSection && (
            <div className="mt-2.5 p-3.5 rounded-xl bg-astro-bg border border-astro-border grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs animate-fade-in">
              <div className="p-2.5 rounded-lg bg-[#FAF6ED] border border-astro-border space-y-1">
                <span className="font-bold text-astro-primary block border-b pb-0.5">Other Calendars & Epoch</span>
                <div className="space-y-0.5 text-[11px]">
                  <div>Vikram Samvat: <strong>{panchang.vikramSamvat}</strong></div>
                  <div>Shaka Samvat: <strong>{panchang.shakaSamvat}</strong></div>
                  <div>Kali Samvat: <strong>{panchang.vikramSamvat + 3102}</strong></div>
                  <div>Lunar Month: <strong>{panchang.masa}</strong></div>
                </div>
              </div>

              <div className="p-2.5 rounded-lg bg-[#FAF6ED] border border-astro-border space-y-1">
                <span className="font-bold text-astro-primary block border-b pb-0.5">Anandadi & Tamil Yoga</span>
                <div className="space-y-0.5 text-[11px]">
                  <div>Anandadi Yoga: <strong>Ananda (Auspicious)</strong></div>
                  <div>Tamil Yoga: <strong>Siddha Yoga</strong></div>
                  <div>Dahana Yoga: <strong>None</strong></div>
                  <div>Amrita Yoga: <strong>Active</strong></div>
                </div>
              </div>

              <div className="p-2.5 rounded-lg bg-[#FAF6ED] border border-astro-border space-y-1">
                <span className="font-bold text-astro-primary block border-b pb-0.5">Moon & Constellation</span>
                <div className="space-y-0.5 text-[11px]">
                  <div>Moon Rashi: <strong>Taurus (Vrishabha)</strong></div>
                  <div>Nakshatra Pada: <strong>Pada 3</strong></div>
                  <div>Sun Sign: <strong>Leo (Simha)</strong></div>
                  <div>Ayana: <strong>{ayana}</strong></div>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default PanchangDrawer;
